import { useMemo } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

import { PermissionItem, RoleWithPermissions } from '../types/user';
import { groupRoles } from '../utils/roleGroups';

interface Props<T extends FieldValues> {
  form: UseFormReturn<T>;
  roles: RoleWithPermissions[];
  isLoading: boolean;
}

// Show only permissions that have a description — product asked to hide
// technical names (resource.action.scope). Permissions without a description
// are skipped intentionally; if any appear in production we'd surface them by
// adding descriptions in the backend rather than falling back to names here.
const dedupePermissions = (perms: PermissionItem[]): PermissionItem[] => {
  const map = new Map<number, PermissionItem>();
  perms.forEach((p) => {
    if (!p.description?.trim()) return;
    if (!map.has(p.id)) map.set(p.id, p);
  });
  return Array.from(map.values()).sort((a, b) => {
    const aLabel = a.description ?? '';
    const bLabel = b.description ?? '';
    return aLabel.localeCompare(bLabel, 'es', { sensitivity: 'base' });
  });
};

const RolesPermissionsSection = <T extends FieldValues>({ form, roles, isLoading }: Props<T>) => {
  const watchedRoleIds = form.watch('roleIds' as never) as unknown as number[] | undefined;
  const roleIds = useMemo(() => watchedRoleIds ?? [], [watchedRoleIds]);
  const groups = useMemo(() => groupRoles(roles), [roles]);

  const selectedRoles = useMemo(() => roles.filter((r) => roleIds.includes(r.id)), [roles, roleIds]);
  const mergedPermissions = useMemo(
    () => dedupePermissions(selectedRoles.flatMap((r) => r.permissions)),
    [selectedRoles]
  );

  const toggleRole = (id: number, checked: boolean): void => {
    const next = checked ? [...roleIds, id] : roleIds.filter((x) => x !== id);
    form.setValue('roleIds' as never, next as never, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-text-subtle font-body">Roles y permisos</h3>

      <div className="grid min-h-0 grid-cols-1 lg:grid-cols-2 gap-4 flex-1">
        <div className="flex min-h-0 flex-col gap-3">
          <Label>Roles</Label>
          {isLoading ? (
            <p className="text-xs text-text-subtle">Cargando roles…</p>
          ) : (
            <ScrollArea
              viewportClassName="min-h-0 flex-1"
              className="flex min-h-24 max-h-96 flex-1 flex-col rounded-md border border-border-subtle"
            >
              <div className="flex flex-col gap-3 p-3">
                {groups.map((group) => (
                  <div key={group.label} className="flex flex-col gap-1.5">
                    <p className="text-xs font-bold uppercase tracking-wide text-text-subtle">{group.label}</p>
                    <ul className="flex flex-col gap-1.5">
                      {group.roles.map((role) => {
                        const checked = roleIds.includes(role.id);
                        return (
                          <li key={role.id} className="flex items-start gap-2">
                            <Checkbox
                              id={`role-${role.id}`}
                              checked={checked}
                              onCheckedChange={(c) => toggleRole(role.id, c === true)}
                              className="mt-0.5"
                            />
                            <Label
                              htmlFor={`role-${role.id}`}
                              className="cursor-pointer text-sm font-normal leading-snug"
                              title={role.description ?? undefined}
                            >
                              {role.displayName}
                            </Label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          <Label>Permisos asignados</Label>
          {selectedRoles.length === 0 ? (
            <p className="text-xs text-text-subtle">Selecciona uno o más roles para ver los permisos asignados.</p>
          ) : (
            <ScrollArea
              viewportClassName="min-h-0 flex-1"
              className="flex min-h-24 max-h-96 flex-1 flex-col rounded-md border border-border-subtle"
            >
              <ul className="flex flex-col gap-1.5 p-3">
                {mergedPermissions.map((p) => (
                  <li key={p.id} className="text-sm text-text-default leading-snug">
                    {p.description}
                  </li>
                ))}
                {mergedPermissions.length === 0 && (
                  <li className="text-xs text-text-subtle">
                    Los roles seleccionados no tienen permisos con descripción.
                  </li>
                )}
              </ul>
            </ScrollArea>
          )}
        </div>
      </div>
    </section>
  );
};

export default RolesPermissionsSection;
